import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { initializeKeys, departmentKeys } from '../query-keys';
import {
  resolveDepartmentManagerContract,
  type ResolveDepartmentManagerRequest,
  type ResolveDepartmentManagerResponse
} from '@bliks-io/contracts';

/**
 * Resolve department manager mutation
 *
 * Manually resolves an unresolved department by assigning a manager.
 * The department is updated in place: `manager` is set and the
 * `managerEmail` hint is cleared.
 *
 * - Invalidates unresolved departments cache
 * - Invalidates departments cache
 *
 * @example
 * ```tsx
 * function AssignManagerDialog({ department }: Props) {
 *   const resolveManager = useResolveDepartmentManager();
 *
 *   const handleAssign = async (managerId: string) => {
 *     await resolveManager.mutateAsync({
 *       id: department._id,
 *       data: { managerId }
 *     });
 *     toast.success('Department resolved successfully');
 *   };
 * }
 * ```
 */
export function useResolveDepartmentManager() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: ResolveDepartmentManagerRequest;
    }): Promise<ResolveDepartmentManagerResponse> => {
      const path = resolveDepartmentManagerContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<ResolveDepartmentManagerResponse>(
        path,
        data
      );
      return response;
    },
    // Awaited (returned) on purpose: the wizard derives its current step from
    // the unresolved-departments cache — navigating away from the resolve
    // sub-step before the refetch lands would transiently bounce back into it.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: initializeKeys.unresolvedDepartments() }),
        queryClient.invalidateQueries({ queryKey: departmentKeys.lists() }),
        // Resolving moves the summary's unresolved count, which feeds the
        // overview's completion-blocked alert.
        queryClient.invalidateQueries({ queryKey: initializeKeys.summary() })
      ])
  });
}
